import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { FeeAmount, Pool, Position } from '@kodiak-finance/v3-sdk'
import { useMemo } from 'react'

import { useGetTokenPrices } from './useTokenPrice'

interface UseTokenPricingParams {
  token0?: Token
  token1?: Token
  feeValue0?: CurrencyAmount<Token>
  feeValue1?: CurrencyAmount<Token>
  liquidity: string
  tickLower: number
  tickUpper: number
  currentTick: number
  feeAmount?: number
  poolLiquidity?: string
  sqrtPriceX96?: string
  isClosedPosition?: boolean
}

interface UseTokenPricingResult {
  pricesLoading: boolean
  tokenPriceMap: { [address: string]: number | null }
  fiatValueOfFees: number | null
  fiatValueOfLiquidity: number | null
  amount0ForDisplay?: CurrencyAmount<Token>
  amount1ForDisplay?: CurrencyAmount<Token>
  retryPrices: any
  pricesError: boolean
}

export function useTokenPricing({
  token0,
  token1,
  feeValue0,
  feeValue1,
  liquidity,
  poolLiquidity,
  tickLower,
  tickUpper,
  currentTick,
  feeAmount,
  sqrtPriceX96,
  isClosedPosition,
}: UseTokenPricingParams): UseTokenPricingResult {
  const tokenAddresses = useMemo(() => {
    if (!token0 || !token1) return []
    return [token0.address, token1.address]
  }, [token0?.address, token1?.address])

  const {
    prices: tokenPrices,
    isLoading: pricesQueryLoading,
    isError: pricesError,
    retry,
  } = useGetTokenPrices(tokenAddresses)

  const pricesLoading = useMemo(() => {
    if (isClosedPosition) return false

    return pricesQueryLoading
  }, [isClosedPosition, pricesQueryLoading])

  const tokenPriceMap = useMemo(() => {
    if (isClosedPosition) return {}
    if (!tokenPrices || !Array.isArray(tokenPrices)) return {}

    const priceMap: { [address: string]: number | null } = {}
    tokenAddresses.forEach((address, index) => {
      priceMap[address] = tokenPrices[index] !== undefined ? tokenPrices[index] : null
    })
    return priceMap
  }, [isClosedPosition, tokenAddresses, tokenPrices])

  const poolTokens: [Token, Token, FeeAmount] | undefined = useMemo(() => {
    if (isClosedPosition) return undefined

    if (token0 && token1 && feeAmount) {
      if (token0.equals(token1)) return undefined
      return token0.sortsBefore(token1)
        ? [token0, token1, feeAmount as FeeAmount]
        : [token1, token0, feeAmount as FeeAmount]
    }
    return undefined
  }, [isClosedPosition, token0, token1, feeAmount])

  const { amount0ForDisplay, amount1ForDisplay } = useMemo(() => {
    if (isClosedPosition) {
      return { amount0ForDisplay: undefined, amount1ForDisplay: undefined }
    }

    if (!poolTokens) {
      return { amount0ForDisplay: undefined, amount1ForDisplay: undefined }
    }

    if (typeof currentTick !== 'number' || typeof tickLower !== 'number' || typeof tickUpper !== 'number') {
      return { amount0ForDisplay: undefined, amount1ForDisplay: undefined }
    }

    if (!feeAmount || !liquidity || !sqrtPriceX96 || !poolLiquidity) {
      return { amount0ForDisplay: undefined, amount1ForDisplay: undefined }
    }

    try {
      const liquidityString = typeof liquidity === 'string' ? liquidity : liquidity.toString()

      const pool = new Pool(poolTokens[0], poolTokens[1], poolTokens[2], sqrtPriceX96, poolLiquidity, currentTick)

      const position = new Position({
        pool,
        liquidity: liquidityString,
        tickLower,
        tickUpper,
      })

      const isInputToken0Sorted = token0 && token1 && token0.sortsBefore(token1)
      const [sortedToken0, sortedToken1] = isInputToken0Sorted ? [token0!, token1!] : [token1!, token0!]

      const mappedAmount0 = token0?.equals(sortedToken0) ? position.amount0 : position.amount1
      const mappedAmount1 = token1?.equals(sortedToken1) ? position.amount1 : position.amount0

      return {
        amount0ForDisplay: mappedAmount0,
        amount1ForDisplay: mappedAmount1,
      }
    } catch (error) {
      console.error('Error with Pool + Position approach:', error)
      return { amount0ForDisplay: undefined, amount1ForDisplay: undefined }
    }
  }, [
    isClosedPosition,
    poolTokens,
    currentTick,
    tickLower,
    tickUpper,
    feeAmount,
    liquidity,
    sqrtPriceX96,
    poolLiquidity,
    token0,
    token1,
  ])

  const fiatValueOfFees = useMemo(() => {
    if (isClosedPosition) return 0

    if (pricesQueryLoading || !feeValue0 || !feeValue1) {
      return null
    }

    if (!token0 || !token1) return 0

    const token0Price = tokenPriceMap[token0.address]
    const token1Price = tokenPriceMap[token1.address]

    try {
      const fee0USD = parseFloat(feeValue0.toExact()) * (token0Price || 0)
      const fee1USD = parseFloat(feeValue1.toExact()) * (token1Price || 0)
      return fee0USD + fee1USD
    } catch (error) {
      console.error('Error calculating fee USD value:', error)
      return 0
    }
  }, [feeValue0, feeValue1, isClosedPosition, token0, token1, tokenPriceMap, pricesQueryLoading])

  const fiatValueOfLiquidity = useMemo(() => {
    if (isClosedPosition) return 0

    if (!amount0ForDisplay || !amount1ForDisplay || !token0 || !token1) return null

    const token0Price = tokenPriceMap[token0.address]
    const token1Price = tokenPriceMap[token1.address]

    if (pricesQueryLoading) {
      return null
    }

    if (token0Price === null || token1Price === null || token0Price === undefined || token1Price === undefined) {
      return 0
    }

    try {
      const amount0USD = parseFloat(amount0ForDisplay.toExact()) * token0Price
      const amount1USD = parseFloat(amount1ForDisplay.toExact()) * token1Price
      return amount0USD + amount1USD
    } catch (error) {
      console.error('Error calculating liquidity USD value:', error)
      return 0
    }
  }, [amount0ForDisplay, amount1ForDisplay, isClosedPosition, pricesQueryLoading, token0, token1, tokenPriceMap])

  return {
    pricesLoading,
    tokenPriceMap,
    fiatValueOfFees,
    fiatValueOfLiquidity,
    amount0ForDisplay,
    amount1ForDisplay,
    retryPrices: retry,
    pricesError,
  }
}
